import type { BankItem } from "../../engine/bank-types";

/**
 * 誤解識別 — the passages that are most often quoted wrongly.
 *
 * The distractors here are not invented: each is a reading that circulates in
 * real life, in textbooks, or in the annotation tradition itself. Naming the
 * misreading and saying exactly where it breaks is the point of the type.
 */
export const MISREAD_ITEMS: BankItem[] = [
  {
    id: "mr-1-3",
    type: "misread",
    refs: [3, 493],
    concepts: ["ren", "xin"],
    difficulty: 2,
    cognitive: "evaluate",
    purpose: "「巧言令色」批的是言色與心不副，不是口才本身",
    stem: "「巧言令色，鮮矣仁」——下面哪個理解站得住？",
    options: [
      { id: "a", text: "言色與內心不副者，少有仁德" },
      { id: "b", text: "會說話、儀容好的人都不可信", why: "孔子並不反對辭令，他說「辭達而已矣」，也稱子貢言語第一。批的是巧與令，即刻意修飾以取悅。" },
      { id: "c", text: "君子應當木訥少言，不修邊幅", why: "「剛毅木訥近仁」是另一面的話，並非要求人人木訥；6.18 更明說文質要相稱。" },
      { id: "d", text: "外表和言辭都不重要，只看內心", why: "《鄉黨》一整篇記孔子的容色進退，可見外在正是內在的表現，不是不重要。" },
    ],
    answerId: "a",
    explanation: "關鍵在「巧」「令」二字：修飾到與真實用心脫節。判準不是話多話少，是言色背後有沒有那份心。",
  },
  {
    id: "mr-1-11",
    type: "misread",
    refs: [11, 86],
    concepts: ["xiao"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "「三年無改於父之道」的限定條件",
    stem: "「三年無改於父之道，可謂孝矣」——哪種讀法偏離了原義？",
    options: [
      { id: "a", text: "無論對錯，三年內一概不許改動" },
      { id: "b", text: "指值得承續的做法，不急著推翻", why: "「道」正是指可以承續的正道，這是本句的正解。" },
      { id: "c", text: "守喪期間哀思未定，不宜大動", why: "這是歷來通行的一種解釋，與原義不衝突。" },
      { id: "d", text: "與孟莊子不改父臣父政是同一路", why: "19.18 曾子稱許孟莊子，正是本章的同類例證。" },
    ],
    answerId: "a",
    explanation: "把「道」讀成「一切做法」，孝就變成了對錯不分的凍結。《論語》談孝始終留著勸諫的位置——這一章限定的是時機與態度，不是是非。",
  },
  {
    id: "mr-2-11",
    type: "misread",
    refs: [27],
    concepts: ["xue", "jiaoxue"],
    difficulty: 2,
    cognitive: "evaluate",
    purpose: "溫故知新的落點在「知新」與「為師」",
    stem: "「溫故而知新，可以為師矣」，最貼近原義的是？",
    options: [
      { id: "a", text: "能從舊學生出新理解，才夠格為師" },
      { id: "b", text: "常常複習學過的內容，就能記得牢", why: "若只是記得牢，接不上「可以為師」——複習得勤並不使人有資格教別人。" },
      { id: "c", text: "既要溫習舊知識，也要學習新知識", why: "「知新」不是再學一批新材料，是從舊材料中看出新東西，兩者用力方向不同。" },
      { id: "d", text: "老師應當不斷更新自己的教學內容", why: "主語是求學者而非老師；「可以為師矣」是結果，不是對象。" },
    ],
    answerId: "a",
    explanation: "為師的門檻不在知道得多，在能不能從舊東西裡長出新見解。只會複述的人可以是好學生，還不是老師。",
  },
  {
    id: "mr-2-12",
    type: "misread",
    refs: [28],
    concepts: ["junzi", "xue"],
    difficulty: 2,
    cognitive: "evaluate",
    purpose: "「君子不器」不是輕視專業",
    stem: "「君子不器」，哪一個理解偏離了原義？",
    options: [
      { id: "a", text: "君子不該學專門技術，那是工匠的事" },
      { id: "b", text: "不像器皿只有一種用途，貴在通貫", why: "這正是本章的正解：反對的是被限定成單一用途，不是反對本事。" },
      { id: "c", text: "與 5.4 稱子貢為瑚璉之器可以相參", why: "孔子稱子貢是貴重的器仍有保留，正是本章的旁證。" },
      { id: "d", text: "人的價值不由他的功能來決定", why: "這是本章可以引申出的意思，並不偏離原義。" },
    ],
    answerId: "a",
    explanation: "把「不器」讀成瞧不起技術，正好倒過來。5.4 是最好的註腳：稱子貢為貴重的器，仍不是最高的評價——因為器再貴，用途也是被限定的。",
  },
  {
    id: "mr-2-16",
    type: "misread",
    refs: [32],
    concepts: ["xue", "zhiren"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "「攻乎異端」的兩種讀法與其代價",
    stem: "「攻乎異端，斯害也已」爭議極大。下面哪一項是可靠的判斷？",
    options: [
      { id: "a", text: "「攻」訓專治，解作攻擊異己則不合" },
      { id: "b", text: "孔子在此明令弟子攻擊一切不同的學派", why: "與 9.4「毋必毋固」、13.23「和而不同」正相衝突，也無法解釋他與隱者的往來。" },
      { id: "c", text: "此章證明孔子主張思想統一", why: "全書並無禁絕異說之例；他對長沮桀溺、楚狂接輿都存而不斥。" },
      { id: "d", text: "「異端」專指後來的楊墨佛老", why: "楊墨在孔子之後，佛老更晚，時代上不可能。" },
    ],
    answerId: "a",
    explanation: "遇到有爭議的章句，穩妥的辦法是拿全書的其他證據來校準：孔子既不強人同，「攻」就更可能是專治而非攻擊。",
  },
  {
    id: "mr-4-8",
    type: "misread",
    refs: [74],
    concepts: ["xue", "yili"],
    difficulty: 2,
    cognitive: "evaluate",
    purpose: "「朝聞道，夕死可矣」不是輕生",
    stem: "「朝聞道，夕死可矣」的意思最接近？",
    options: [
      { id: "a", text: "以極端說法衡量聞道之重，此生無憾" },
      { id: "b", text: "人生短暫，應當及時行樂", why: "方向全反：他說的是為道可以捨命，不是趁早享受。" },
      { id: "c", text: "求道者應當不惜自盡以殉道", why: "這是把修辭當指令；15.9「殺身以成仁」有明確語境，也不是主動求死。" },
      { id: "d", text: "早晨學到的東西，晚上就該忘掉", why: "與「聞道」的分量完全不相稱。" },
    ],
    answerId: "a",
    explanation: "這是分量的比喻，不是行動的指示。它要說的是：如果連道都聞得到了，生命長短這件事就不再是最重要的問題。",
  },
  {
    id: "mr-4-15",
    type: "misread",
    refs: [81],
    concepts: ["shu", "ren"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "「忠」在忠恕中不是忠君",
    stem: "曾子以「忠恕」解「吾道一以貫之」，這裡的「忠」指什麼？",
    options: [
      { id: "a", text: "盡己：竭盡自己這一分，與恕相配" },
      { id: "b", text: "忠於君主，臣事君以忠", why: "3.19 的「忠」確指事君，但此處與「恕」對舉，講的是待人接物的一體兩面，範圍大得多。" },
      { id: "c", text: "忠厚老實的性情", why: "「忠」在此是用力的方式，不是性格描述。" },
      { id: "d", text: "忠於學說，不改師門宗旨", why: "章旨在說一貫之道的內容，不在門派立場。" },
    ],
    answerId: "a",
    explanation: "盡己為忠、推己為恕：一個向內用力到盡頭，一個向外推及他人。合起來才是那條貫穿全部的線。",
  },
  {
    id: "mr-4-16",
    type: "misread",
    refs: [82],
    concepts: ["yili", "junzi"],
    difficulty: 2,
    cognitive: "evaluate",
    purpose: "喻於義不是禁止談利",
    stem: "「君子喻於義，小人喻於利」——哪個理解偏離了原義？",
    options: [
      { id: "a", text: "君子不可談論利益，一談利即為小人" },
      { id: "b", text: "「喻」是明白：兩者先算的帳不同", why: "這正是本句的正解，講的是判斷時的優先次序。" },
      { id: "c", text: "與 4.5 相參，孔子只要求以其道得之", why: "4.5 明說富貴是人之所欲，只限制取得的方式，可以互證。" },
      { id: "d", text: "此處君子小人是德行之分非階級", why: "《論語》中君子小人的對舉幾乎全是德行之分，此說不誤。" },
    ],
    answerId: "a",
    explanation: "這一句是判準之分，不是話題禁令。同一件有利可圖的事，君子先問該不該，小人先問划不划算——差別在次序，不在要不要利。",
  },
  {
    id: "mr-6-29",
    type: "misread",
    refs: [149, 489],
    concepts: ["zhongyong"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "中庸不是折衷，鄉愿是它的假貨",
    stem: "把「中庸」理解成「不得罪人、兩邊都不站」，錯在哪裡？",
    options: [
      { id: "a", text: "那正是鄉愿；中庸是隨事求恰好" },
      { id: "b", text: "沒有錯，中庸本來就是與人為善", why: "若中庸即是誰都不得罪，孔子就不會另立「鄉愿」一詞來罵它。" },
      { id: "c", text: "錯在中庸要求永遠站在多數那一邊", why: "13.24 明說「鄉人皆好之，未可也」——多數的好惡不是判準。" },
      { id: "d", text: "錯在中庸只適用於治國，不適用於處世", why: "6.29 稱之為「德」，是通用的德行，不限於政事。" },
    ],
    answerId: "a",
    explanation: "鄉愿之所以是「德之賊」，正因為它看起來像中庸：面面俱到、無人指摘。差別在於中庸有一個外在的「恰好」要對準，鄉愿只對準別人的臉色。",
  },
  {
    id: "mr-7-22",
    type: "misread",
    refs: [172],
    concepts: ["xue", "zhiren"],
    difficulty: 2,
    cognitive: "evaluate",
    purpose: "三人行的後半句才是重點",
    stem: "「三人行，必有我師焉」常被單獨引用。完整讀下去，重點是？",
    options: [
      { id: "a", text: "擇善而從，不善者亦可據以自改" },
      { id: "b", text: "任何人都比自己強，要保持謙虛", why: "原句沒有說別人都強；它說的是無論善不善，都能取為借鏡。" },
      { id: "c", text: "要多結交比自己優秀的朋友", why: "那是 1.8「無友不如己者」的話題，本章不談擇友。" },
      { id: "d", text: "三個人裡一定有一個能當老師的人", why: "「三人」是舉數言少，不是說三人中必有一賢。" },
    ],
    answerId: "a",
    explanation: "只引前半句，聽起來是謙虛；連著後半句讀，是方法：好的照著做，不好的拿來對照自己改。負面榜樣的用處，在這裡被說明白了。",
  },
  {
    id: "mr-8-9",
    type: "misread",
    refs: [197],
    concepts: ["zheng", "jiaoxue"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "「民可使由之」與愚民說的距離",
    stem: "「民可使由之，不可使知之」被指為愚民主張。較穩妥的判斷是？",
    options: [
      { id: "a", text: "斷句訓釋分歧極大，孤證不足定案" },
      { id: "b", text: "孔子確實主張愚民，這是他思想的封建性", why: "把有爭議的孤句當定論，又與 15.39「有教無類」、17.2「習相遠也」的立場衝突。" },
      { id: "c", text: "「不可」是「不能夠」，指百姓天生不能明白道理", why: "這是諸說之一，但同樣是解釋而非定論；且與他辦學授徒的實踐不合。" },
      { id: "d", text: "此句是後人竄入，不必理會", why: "沒有版本證據支持刪削，迴避也不是讀法。" },
    ],
    answerId: "a",
    explanation: "碰到疑難章句，方法比結論重要：先承認歧解存在，再用作者其他明確的立場來校準。這比替古人下一個痛快的判決有用得多。",
  },
  {
    id: "mr-8-14",
    type: "misread",
    refs: [202, 402],
    concepts: ["chushi", "zheng"],
    difficulty: 2,
    cognitive: "evaluate",
    purpose: "不在其位不是明哲保身",
    stem: "「不在其位，不謀其政」，最貼近原義的理解是？",
    options: [
      { id: "a", text: "不越權干預本職以外的事，是講分際" },
      { id: "b", text: "不在位就不必關心公共事務，明哲保身即可", why: "孔子不在位時仍論政、仍「知其不可而為之」；14.21 更沐浴而朝請討陳恆。" },
      { id: "c", text: "沒有職位的人沒有資格發表意見", why: "全書大量是孔子無位時的議論，這一讀法會使《論語》自我否定。" },
      { id: "d", text: "做官要專心本職，不可兼任他職", why: "講的是謀與不謀，不是兼職制度。" },
    ],
    answerId: "a",
    explanation: "分際與冷漠是兩回事。不越位是尊重職守，不等於放棄關心——孔子一生正是無位而不肯不問。",
  },
  {
    id: "mr-9-25",
    type: "misread",
    refs: [8, 234],
    concepts: ["xin", "zhiren"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "「無友不如己者」的合理讀法",
    stem: "「無友不如己者」若讀成「不跟不如自己的人交朋友」，會有什麼問題？",
    options: [
      { id: "a", text: "會推出人人高攀、無人可交的悖論" },
      { id: "b", text: "沒有問題，孔子確實主張只結交勝過自己的人", why: "若人人如此，則無人能成為別人的朋友——這個讀法自我瓦解。" },
      { id: "c", text: "問題在於孔子看不起窮人", why: "「如己」講的是德學不是貧富；9.27 他還稱許衣敝縕袍的子路。" },
      { id: "d", text: "問題在於這句是子夏說的，不能代表孔子", why: "1.8 與 9.25 皆繫於孔子，此說於文本無據。" },
    ],
    answerId: "a",
    explanation: "一個讀法若推到底會自相矛盾，通常是讀法有問題。較通的解是「不要跟志趣不相類的人相結」，重點在同道，不在高低。",
  },
  {
    id: "mr-11-16",
    type: "misread",
    refs: [286],
    concepts: ["zhongyong", "jiaoxue"],
    difficulty: 2,
    cognitive: "evaluate",
    purpose: "過猶不及不是折衷取中",
    stem: "子貢問師與商孰賢，孔子說「過猶不及」。這句話的意思是？",
    options: [
      { id: "a", text: "過與不及同樣是失，兩人都不合格" },
      { id: "b", text: "子張勝過子夏，因為過總比不及好", why: "子貢正是這樣追問「然則師愈與」，孔子當即否定。" },
      { id: "c", text: "兩人一樣好，各有長處", why: "「猶」是同樣，指同樣不合格，不是同樣可取。" },
      { id: "d", text: "凡事取兩人的中間值就對了", why: "恰好不是算術平均；中庸要對準的是事情本身，不是兩個人的中點。" },
    ],
    answerId: "a",
    explanation: "子貢的追問暴露了常見直覺：過頭總比不夠強。孔子不接受——衡量的基準是事情該有的樣子，不是與別人比高低。",
  },
  {
    id: "mr-12-1",
    type: "misread",
    refs: [323],
    concepts: ["ren", "li"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "克己不是滅己",
    stem: "「克己復禮為仁」中的「克己」，哪個理解偏離了原義？",
    options: [
      { id: "a", text: "徹底消滅個人欲望，不再有所想要" },
      { id: "b", text: "約束不合禮的部分，回到分寸上", why: "「克」是約束而非消滅，這正是本句的正解。" },
      { id: "c", text: "下文非禮勿視聽言動即其操作清單", why: "四勿正是克己的具體條目，與原義相合。" },
      { id: "d", text: "為仁由己表明此事由自己發動", why: "同章下句明言「為仁由己，而由人乎哉」，不誤。" },
    ],
    answerId: "a",
    explanation: "克己與為仁由己在同一章裡並存，這不是矛盾：要約束的是逾禮的部分，而發動約束的正是你自己。取消了自我，這章的後半句就沒有主語了。",
  },
  {
    id: "mr-12-11",
    type: "misread",
    refs: [333],
    concepts: ["zhengming", "zheng"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "君君臣臣是雙向要求",
    stem: "「君君，臣臣，父父，子子」是不是單方面要求下位者服從？",
    options: [
      { id: "a", text: "不是：四句同一句式，責任是雙向的" },
      { id: "b", text: "是：這是等級秩序的宣示，下位者須絕對服從", why: "第一個字就是「君君」——要求先落在君身上。3.19 更明說「君使臣以禮」在前。" },
      { id: "c", text: "是：孔子答齊景公，就是勸他加強統治", why: "齊景公的問題正出在陳氏專權與繼嗣不定；孔子的答案是名實相符，不是加強壓制。" },
      { id: "d", text: "不是：這句其實在勸君主放權", why: "章旨在名實相符，不涉權力分配。" },
    ],
    answerId: "a",
    explanation: "名分是相互構成的：沒有像樣的君，「臣」這個角色也就失去了對象。這也是 3.19 的同一結構——先禮而後忠。",
  },
  {
    id: "mr-12-5",
    type: "misread",
    refs: [327],
    concepts: ["tianming", "junzi"],
    difficulty: 2,
    cognitive: "analyze",
    purpose: "分辨誰在說話",
    stem: "「死生有命，富貴在天」「四海之內，皆兄弟也」——這些話是誰說的？",
    context: "司马牛忧曰：“人皆有兄弟，我独亡。”子夏曰：“商闻之矣：死生有命，富贵在天……”",
    options: [
      { id: "a", text: "子夏說的，且自稱是「商聞之矣」" },
      { id: "b", text: "孔子親口對司馬牛說的", why: "章中說話的是子夏；司馬牛憂的對象也不是孔子。" },
      { id: "c", text: "司馬牛的自我寬解", why: "司馬牛只說了「人皆有兄弟，我獨亡」，安慰是子夏給的。" },
      { id: "d", text: "《論語》編者的按語", why: "《論語》體例中無此類按語，全章是對話記錄。" },
    ],
    answerId: "a",
    explanation: "引《論語》前先看清說話人：這兩句流傳最廣，卻既非孔子所言，子夏本人還註明是聽來的。把它當孔子的天命論來批評或引用，都站不住。",
  },
  {
    id: "mr-13-5",
    type: "misread",
    refs: [351],
    concepts: ["shijiao", "xue"],
    difficulty: 2,
    cognitive: "evaluate",
    purpose: "背得多不等於學會",
    stem: "「誦《詩》三百，授之以政，不達；使於四方，不能專對；雖多，亦奚以為？」批評的是？",
    options: [
      { id: "a", text: "背熟卻不能用於政事與應對" },
      { id: "b", text: "《詩》本身沒有實用價值，不必多讀", why: "孔子極重《詩》，17.9 列了興觀群怨四項功能；批的是讀法不是《詩》。" },
      { id: "c", text: "背誦數量不夠，三百篇還太少", why: "「雖多」正說明數量已經不少，問題不在多寡。" },
      { id: "d", text: "應當專攻政事，不必學文學", why: "他要的正是《詩》能用於政事，不是二選一。" },
    ],
    answerId: "a",
    explanation: "這是全書對「學會了沒有」給出的最硬的檢驗標準：拿去辦事、拿去應對，用得出來才算數。背誦量不是成績。",
  },
  {
    id: "mr-14-38",
    type: "misread",
    refs: [414],
    concepts: ["chushi", "tianming"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "「知其不可而為之」是外人的評語",
    stem: "「是知其不可而為之者與？」這句話的性質是？",
    options: [
      { id: "a", text: "守門人的評語，點出他明知不可仍為" },
      { id: "b", text: "孔子的自述，表明他固執不通", why: "說話的是石門的晨門，不是孔子；語氣也非貶。" },
      { id: "c", text: "子路對老師的抱怨", why: "子路只回答「自孔氏」，評語出自晨門。" },
      { id: "d", text: "讚美孔子有先見之明，早知不可", why: "重點不在預知，在明知不可仍為。" },
    ],
    answerId: "a",
    explanation: "《論語》裡最傳神的評語常出自路人。這一句與 18.6 長沮桀溺「滔滔者天下皆是也」對照著讀，是隱者與孔子兩種人生選擇的正面相遇。",
  },
  {
    id: "mr-15-39",
    type: "misread",
    refs: [459, 141],
    concepts: ["jiaoxue"],
    difficulty: 2,
    cognitive: "evaluate",
    purpose: "有教無類與因材施教不矛盾",
    stem: "「有教無類」與「中人以下，不可以語上也」是否矛盾？",
    options: [
      { id: "a", text: "不矛盾：一講收不收，一講怎麼教" },
      { id: "b", text: "矛盾：孔子前後主張不一", why: "兩句回答的不是同一個問題，衝突是把「教不教」和「怎麼教」混為一談造成的。" },
      { id: "c", text: "不矛盾：後一句是說中人以下不必教", why: "「不可以語上」是不對他講高深的，不是不教。" },
      { id: "d", text: "矛盾：所以「有教無類」應解作按類施教", why: "為消解衝突而改字義，反而丟掉了「無類」最有力的一層。" },
    ],
    answerId: "a",
    explanation: "門要對所有人開，這是「無類」；進門之後各講各的，這是因材施教。11.22 同一問題給子路和冉有相反的答案，正是後者的樣本。",
  },
  {
    id: "mr-16-1",
    type: "misread",
    refs: [463],
    concepts: ["zheng", "yili"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "「不患寡而患不均」的均不是平均",
    stem: "「不患寡而患不均，不患貧而患不安」——「均」指的是？",
    options: [
      { id: "a", text: "各得其分，不是人人數量相等" },
      { id: "b", text: "絕對平均，人人所得完全一樣", why: "先秦的「均」是各得其宜；孔子講的是分配是否合理，不是量的相同。" },
      { id: "c", text: "均衡發展，各行各業並重", why: "章旨在季氏將伐顓臾的斂財擴張，不談產業。" },
      { id: "d", text: "平均主義的最早主張，反對一切差等", why: "《論語》處處講分際等差，此讀法與全書扞格。" },
    ],
    answerId: "a",
    explanation: "這一章是孔子責備冉有助季氏的場合。他反對的是失其分的攫取，不是主張齊頭平等——後面「修文德以來之」才是他給的方案。",
  },
  {
    id: "mr-17-4",
    type: "misread",
    refs: [480],
    concepts: ["jiaoxue", "li"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "「割雞焉用牛刀」是玩笑而非否定",
    stem: "子游在武城以禮樂為治，孔子笑說「割雞焉用牛刀」。這句話該怎麼看？",
    options: [
      { id: "a", text: "是玩笑話，孔子隨即自承前言戲之" },
      { id: "b", text: "孔子認為小地方不必行禮樂，是嚴肅的批評", why: "他隨即自承是開玩笑，並肯定子游說得對。" },
      { id: "c", text: "孔子責備子游不自量力", why: "章末的收束明確站在子游一邊。" },
      { id: "d", text: "孔子認為禮樂只適用於大國", why: "若如此，他就不會承認子游的引述是對的。" },
    ],
    answerId: "a",
    explanation: "難得的一章：孔子開了個玩笑，學生當場引老師自己的話反駁，老師立刻認錯。讀《論語》要留意這種語氣層次，不然會把玩笑當教條。",
  },
  {
    id: "mr-17-25",
    type: "misread",
    refs: [501],
    concepts: ["zhiren"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "面對爭議章句的處理方式",
    stem: "「唯女子與小人為難養也」爭議極大。較負責任的處理是？",
    options: [
      { id: "a", text: "說明訓釋分歧，也不迴避時代局限" },
      { id: "b", text: "直接認定孔子歧視女性，全書因此不必再讀", why: "以一句判定全書，既非讀書之法，也放棄了其餘章句的價值。" },
      { id: "c", text: "強行解釋成褒義，證明孔子並無此意", why: "為維護而曲解，等於放棄文本的真實；這與「毋意毋必」相違。" },
      { id: "d", text: "此句必為後人竄入，應當刪去", why: "無版本依據的刪削，是用主觀取代考證。" },
    ],
    answerId: "a",
    explanation: "讀古書會遇到與今日價值衝突的地方。既不美化也不簡化，把訓釋分歧與時代條件一併呈現——這本身就是一項要練的能力。",
  },
  {
    id: "mr-19-13",
    type: "misread",
    refs: [526],
    concepts: ["xue", "chushi"],
    difficulty: 2,
    cognitive: "evaluate",
    purpose: "「學而優則仕」的優不是優秀",
    stem: "「仕而優則學，學而優則仕」的「優」怎麼講？",
    options: [
      { id: "a", text: "有餘力：兩句互文，學事互相補給" },
      { id: "b", text: "優秀：學得好就可以做官", why: "只取後半句、又把「優」讀成優秀，就丟掉了前半句「仕而優則學」的對稱。" },
      { id: "c", text: "優先：應當先學後仕", why: "兩句互相往返，沒有規定單一次序。" },
      { id: "d", text: "優待：學得好的人應受優待", why: "與句式和上下文都不合。" },
    ],
    answerId: "a",
    explanation: "這句是子夏說的，而且常被砍掉前半句來用。復原成一對，它講的是學與事互相補給，不是「讀書為做官」的階梯。",
  },
  {
    id: "mr-10-8",
    type: "misread",
    refs: [248],
    concepts: ["li"],
    difficulty: 2,
    cognitive: "evaluate",
    purpose: "「食不厭精」不是講究口腹",
    stem: "「食不厭精，膾不厭細」常被當成孔子講究飲食的證據。《鄉黨》全篇的語境提示什麼？",
    options: [
      { id: "a", text: "全篇記禮儀，此處講祭食的潔敬" },
      { id: "b", text: "孔子確實生活奢侈，講究飲食精細", why: "同篇還有「食無求飽」「飯疏食飲水，樂亦在其中」的記載，奢侈說與全書不合。" },
      { id: "c", text: "這是後人偽託，用以貶低孔子", why: "無版本依據；且置於《鄉黨》禮儀語境中並無不合。" },
      { id: "d", text: "說明孔子重視養生", why: "篇中所記是儀節而非養生法。" },
    ],
    answerId: "a",
    explanation: "《鄉黨》是全書最容易誤讀的一篇：孤立地摘句，它像一份生活講究清單；放回禮儀語境，它記的是「敬」如何落到吃穿坐臥的每個細節上。",
  },
  {
    id: "mr-6-27",
    type: "misread",
    refs: [147, 337],
    concepts: ["xue", "li"],
    difficulty: 2,
    cognitive: "understand",
    purpose: "博文約禮是一對",
    stem: "「博學於文，約之以禮」——「約」的作用是？",
    options: [
      { id: "a", text: "收束：用禮把博學收攏到分寸上" },
      { id: "b", text: "簡約：學問要學得少而精", why: "上句正是「博學」，與少而精相反。" },
      { id: "c", text: "約定：與人約定共同守禮", why: "「約」在此是約束收束，不是締約。" },
      { id: "d", text: "節約：學習要節省時間與精力", why: "與本句文義無涉，也接不上「博學於文」。" },
    ],
    answerId: "a",
    explanation: "博與約是一組動作：先放開去學，再用禮收回來。只博不約會散，只約不博會窄——這也是顏淵形容孔子教法的原話（9.11）。",
  },
  {
    id: "mr-7-1b",
    type: "misread",
    refs: [151, 39],
    concepts: ["xue", "li"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "好古不等於反對變革",
    stem: "「信而好古」是否意味孔子反對一切變革？",
    options: [
      { id: "a", text: "不是：他明說禮有損益，也接受變通" },
      { id: "b", text: "是：他主張完全恢復周制，不容更改", why: "9.3「麻冕，禮也；今也純，儉，吾從眾」正是他自己接受更改的例子。" },
      { id: "c", text: "是：所以他說「述而不作」", why: "「不作」指不擅自創制，與能否損益是兩回事。" },
      { id: "d", text: "不是：他其實不重視古制，只是說說", why: "他確實重古，只是重古與可損益並不衝突。" },
    ],
    answerId: "a",
    explanation: "判斷一個人的立場，要拿他實際做的取捨來校驗。孔子在麻冕上從眾、在拜下上違眾——標準不是新舊，是合不合禮的本意。",
  },
  {
    id: "mr-9-11",
    type: "misread",
    refs: [220],
    concepts: ["jiaoxue", "xue"],
    difficulty: 2,
    cognitive: "understand",
    purpose: "「循循然善誘」的具體所指",
    stem: "顏淵說孔子「循循然善誘人，博我以文，約我以禮」。「善誘」具體指？",
    options: [
      { id: "a", text: "按次序引導：先博以文，後約以禮" },
      { id: "b", text: "善於用言語打動人，讓人心服", why: "顏淵隨即說明的是「博我以文，約我以禮」，講的是次序與方法，不是口才。" },
      { id: "c", text: "善於誘導學生說出自己的想法", why: "那是啟發的一面，但顏淵此處點的是博約兩步。" },
      { id: "d", text: "循規蹈矩地按課本教學", why: "「循循」是有次序，不是守成規。" },
    ],
    answerId: "a",
    explanation: "這是學生對老師教法的第一手描述，也是「博文約禮」最好的旁證：顏淵說完「欲罷不能」——好的次序會讓人停不下來。",
  },
  {
    id: "mr-15-9",
    type: "misread",
    refs: [429],
    concepts: ["ren", "yili"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "殺身成仁不是鼓勵犧牲",
    stem: "「無求生以害仁，有殺身以成仁」的重點是？",
    options: [
      { id: "a", text: "極端處境下不可為活命而毀掉仁" },
      { id: "b", text: "鼓勵人主動犧牲生命以成就名節", why: "「有」是「有這樣的情形」，是對極端處境的描述，不是號召。" },
      { id: "c", text: "生命不重要，仁德才重要", why: "孔子從不輕生，11.12 還把生的問題放在死之前。" },
      { id: "d", text: "只有死了才能成就仁", why: "全書講仁多在日常行事，「我欲仁，斯仁至矣」就在眼前。" },
    ],
    answerId: "a",
    explanation: "整句的主詞是「志士仁人」在極端處境下的底線，不是常態的行動指南。把極限情況的底線當成日常的目標，是讀古書最常見的失焦。",
  },
  {
    id: "mr-3-1",
    type: "misread",
    refs: [41],
    concepts: ["li", "zhengming"],
    difficulty: 2,
    cognitive: "analyze",
    purpose: "「是可忍孰不可忍」的主語",
    stem: "「八佾舞於庭，是可忍也，孰不可忍也？」——孔子氣的是什麼？",
    options: [
      { id: "a", text: "以大夫之身用天子之樂，是僭越" },
      { id: "b", text: "季氏舞蹈的動作不合規矩，跳得難看", why: "問題在規格等級（八佾為天子之數），不在舞得好不好。" },
      { id: "c", text: "孔子不喜歡樂舞這種娛樂", why: "他極重樂，聞韶三月不知肉味。" },
      { id: "d", text: "季氏在自家庭院辦活動擾民", why: "章旨全在僭禮，與擾民無涉。" },
    ],
    answerId: "a",
    explanation: "「忍」歷來有「容忍」與「忍心」兩讀，但無論哪一讀，怒點都在僭越。這一章是理解孔子為何看重「正名」的入口。",
  },
  {
    id: "mr-11-26",
    type: "misread",
    refs: [296],
    concepts: ["chushi", "li"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "「吾與點也」不是否定其他三人",
    stem: "四子言志，孔子喟然嘆「吾與點也」。這是否表示他否定了子路、冉有、公西華的志向？",
    options: [
      { id: "a", text: "不是：他隨後逐一評論，各有肯定" },
      { id: "b", text: "是：他認為從政都不足取，只有隱逸可貴", why: "他一生求仕，也稱許冉有、公西華可以治賦、可以為相。" },
      { id: "c", text: "是：曾晳的答案最高明，其餘都錯", why: "章末的評語明白區分了各人，並無全盤否定。" },
      { id: "d", text: "不是：他只是喜歡春天的景色", why: "「與」是贊同其志，不止於景物。" },
    ],
    answerId: "a",
    explanation: "全書最長也最有畫面的一章。讀到「吾與點也」就收，會錯過後半段的逐一點評——孔子欣賞曾晳的從容，卻沒有取消其他人的路。",
  },
];
